import { useState, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import APIs from '../../../../../application/APIs';
import { Placeholder, PlaceholderStyle, PopupState } from './types';

const DEFAULT_STYLE: PlaceholderStyle = {
  font_size: 12,
  font_name: 'Helvetica',
  color: '#000000',
};

interface UsePopupStateProps {
  templateId: string;
  getCurrentPage: () => number;
  onPlaceholderCreated: (placeholder: Placeholder) => void;
}

export const usePopupState = ({ templateId, getCurrentPage, onPlaceholderCreated }: UsePopupStateProps) => {
  const [popupState, setPopupStateInternal] = useState<PopupState | null>(null);

  // Keep a ref to popupState so async callbacks can read latest value
  const popupRef = useRef(popupState);
  popupRef.current = popupState;

  const openPopup = useCallback((screenX: number, screenY: number, pdfX: number, pdfY: number) => {
    setPopupStateInternal({
      mode: 'variable',
      step: 'columns',
      screenPosition: { x: screenX, y: screenY },
      pdfPosition: { x: pdfX, y: pdfY },
      selectedColumn: null,
      columnValues: [],
      loadingValues: false,
    });
  }, []);

  const setPopupMode = useCallback((mode: 'variable' | 'static_text' | 'custom_variable') => {
    setPopupStateInternal(s => s ? { ...s, mode } : null);
  }, []);

  const closePopup = useCallback(() => {
    setPopupStateInternal(null);
  }, []);

  const goBackToColumns = useCallback(() => {
    setPopupStateInternal(s => s ? {
      ...s,
      step: 'columns',
      selectedColumn: null,
      columnValues: [],
    } : null);
  }, []);

  // Helper to detect font at a click position
  const detectFontAtPosition = async (currentPage: number, pdfX: number, pdfY: number): Promise<PlaceholderStyle> => {
    let detectedStyle: PlaceholderStyle = { ...DEFAULT_STYLE };
    try {
      const fontInfo = await APIs.detectFontAtRect(templateId, currentPage, {
        x: pdfX - 5,
        y: pdfY - 5,
        w: 10,
        h: 10,
      });
      detectedStyle = {
        font_size: fontInfo.font_size,
        font_name: fontInfo.font_name,
        align: 'left',
        color: fontInfo.color,
      };
    } catch (error) {
      console.error('Failed to detect font, using defaults:', error);
    }
    return detectedStyle;
  };

  // Create placeholder immediately when column is selected (no value selection step)
  const selectColumnInPopup = useCallback(async (columnKey: string) => {
    const currentPopup = popupRef.current;
    if (!currentPopup) return;

    const { pdfPosition } = currentPopup;
    const currentPage = getCurrentPage();

    const detectedStyle = await detectFontAtPosition(currentPage, pdfPosition.x, pdfPosition.y);

    const newPlaceholder: Placeholder = {
      id: uuidv4(),
      type: 'placeholder',
      db_column: columnKey,
      page: currentPage,
      rect: {
        x: pdfPosition.x,
        y: pdfPosition.y,
        w: 150,
        h: detectedStyle.font_size * 1.2,
      },
      style: detectedStyle,
      currentValue: undefined, // No value yet - will be filled by auto-fill
    };

    onPlaceholderCreated(newPlaceholder);
    setPopupStateInternal(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, getCurrentPage, onPlaceholderCreated]);

  // Create static text (fixed value, not a variable)
  const createStaticTextInPopup = useCallback(async (text: string) => {
    const currentPopup = popupRef.current;
    if (!currentPopup) return;

    const { pdfPosition } = currentPopup;
    const currentPage = getCurrentPage();

    const detectedStyle = await detectFontAtPosition(currentPage, pdfPosition.x, pdfPosition.y);

    const newPlaceholder: Placeholder = {
      id: uuidv4(),
      type: 'static_text',
      db_column: '', // Not used for static text
      static_value: text,
      page: currentPage,
      rect: {
        x: pdfPosition.x,
        y: pdfPosition.y,
        w: Math.max(150, text.length * 8), // Estimate width based on text length
        h: detectedStyle.font_size * 1.2,
      },
      style: detectedStyle,
      currentValue: text, // Static text is always its own value
    };

    onPlaceholderCreated(newPlaceholder);
    setPopupStateInternal(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, getCurrentPage, onPlaceholderCreated]);

  // Create custom variable placeholder
  const createCustomVariableInPopup = useCallback(async (name: string) => {
    const currentPopup = popupRef.current;
    if (!currentPopup) return;

    const { pdfPosition } = currentPopup;
    const currentPage = getCurrentPage();

    const detectedStyle = await detectFontAtPosition(currentPage, pdfPosition.x, pdfPosition.y);

    const newPlaceholder: Placeholder = {
      id: uuidv4(),
      type: 'custom_variable',
      db_column: '',
      custom_variable_name: name,
      page: currentPage,
      rect: {
        x: pdfPosition.x,
        y: pdfPosition.y,
        w: Math.max(150, name.length * 8),
        h: detectedStyle.font_size * 1.2,
      },
      style: detectedStyle,
    };

    onPlaceholderCreated(newPlaceholder);
    setPopupStateInternal(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, getCurrentPage, onPlaceholderCreated]);

  const createPlaceholderWithValue = useCallback(async (valueObj: { identifier: string; value: string }) => {
    const currentPopup = popupRef.current;
    if (!currentPopup) return;

    const { pdfPosition, selectedColumn } = currentPopup;
    if (!selectedColumn) return;

    const currentPage = getCurrentPage();

    const detectedStyle = await detectFontAtPosition(currentPage, pdfPosition.x, pdfPosition.y);

    const newPlaceholder: Placeholder = {
      id: uuidv4(),
      type: 'placeholder',
      db_column: selectedColumn,
      page: currentPage,
      rect: {
        x: pdfPosition.x,
        y: pdfPosition.y,
        w: 150,
        h: detectedStyle.font_size * 1.2,
      },
      style: detectedStyle,
      currentValue: valueObj.value,
      sourceRecordId: valueObj.identifier,
    };

    onPlaceholderCreated(newPlaceholder);
    setPopupStateInternal(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, getCurrentPage, onPlaceholderCreated]);

  return {
    popupState,
    openPopup,
    closePopup,
    setPopupMode,
    selectColumnInPopup,
    createStaticTextInPopup,
    createCustomVariableInPopup,
    goBackToColumns,
    createPlaceholderWithValue,
  };
};
